import {
  Dialog,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  Box,
  Button,
  DialogContent,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogActions,
  DialogContentText,
  Typography,
  SxProps,
} from "@mui/material";
import { InventoryData, PlanTypes } from "../../../../types/inventoryData";
import { CloseRounded } from "@mui/icons-material";
import { FC, useState } from "react";
import { BatchEditProducts } from "../../../../api/inventory";
import { useInventory } from "../../InventoryProvider";
import { Theme } from "@emotion/react";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { productStatusOptions } from "../../../../helpers/selectOptions";
import { HeadCell } from "../Table/TableTypes";
import { ErrorCodes } from "../../../../types/enums";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { inventoryRules } from "../../../../helpers/validationSchemes";
import { useTranslation } from "react-i18next";

type BatchEditTypes = {
  open: boolean;
  handleClose: () => void;
};

const BatchEditPopup: React.FC<BatchEditTypes> = ({ open, handleClose }) => {
  const {
    selectedProductIds,
    setSelectedProductIds,
    updateManyRows,
    rows,
    headCells,
    plans,
    fetchPlans,
    isPhoneSize,
  } = useInventory();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm<InventoryData>({
    defaultValues: {
      status: undefined,
      location: "",
      note: "",
      plan: undefined,
    },
    mode: "onChange",
  });

  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();

  const style = {
    // position: "absolute" as "absolute",
    // top: "50%",
    // left: "50%",
    // transform: "translate(-50%, -50%)",
    // width: "70%",
    // height: "90%",
    // bgcolor: "background.paper",
    // border: "none",
    boxShadow: isPhoneSize ? 0 : 24,
    p: 4,
    // borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    overflow: isPhoneSize ? "none" : "hidden",
    gap: 3,
  };

  const returnSelectedRows = () => {
    const foundRows = selectedProductIds.map((rowId) =>
      rows.find((r) => r.productId === rowId)
    );
    return foundRows == undefined ? [] : (foundRows as InventoryData[]);
  };

  const [rowsSelected, setRowsSelected] = useState(returnSelectedRows());

  const handleSaveChanges: SubmitHandler<InventoryData> = async (data) => {
    const updatedRows = rowsSelected.map((row) => {
      if (!row) return row;
      const updatedRow = { ...row };
      const dirtyFieldsKeys = Object.keys(dirtyFields);
      Object.entries(data).forEach(([key, value]) => {
        if (dirtyFieldsKeys.includes(key)) {
          (updatedRow as any)[key] = value;
        }
      });
      return updatedRow;
    });

    try {
      const updatedProduct: InventoryData[] = await BatchEditProducts(
        updatedRows
      );
      setRowsSelected(updatedProduct);
      updateManyRows(updatedProduct);
      setSelectedProductIds(updatedProduct.map((r) => r.productId));
      fetchPlans();
      reset();
      showSnackbar(t("inventory.successupdate"), "success");
      // handleClose();
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.ProductNotFound:
            showSnackbar(t("errors.ProductNotFound"), "error");
            break;
          default:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
        }
      } else {
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    }
  };

  const TableTypography: FC<{
    children: any;
    sx?: SxProps<Theme> | undefined;
  }> = ({ children, sx, ...rest }) => {
    return (
      <Typography variant="body2" sx={sx} {...rest}>
        {children}
      </Typography>
    );
  };

  const [confirmCloseBoolean, setConfirmCloseBoolean] =
    useState<boolean>(false);

  const ConfirmCloseDialog = () => {
    return (
      <>
        <Dialog
          open={confirmCloseBoolean}
          onClose={() => setConfirmCloseBoolean(false)}
        >
          <DialogTitle>{t("inventory.unsavedChangesTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("inventory.unsavedChanges")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setConfirmCloseBoolean(false)}
              autoFocus
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setConfirmCloseBoolean(false);
                handleClose();
              }}
            >
              {t("inventory.closeWithoutSaving")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const handleCloseConfirm = () => {
    if (isDirty) {
      setConfirmCloseBoolean(true);
    } else {
      handleClose();
    }
  };

  const AdvEditTableCell: FC<{
    headCell: HeadCell;
    row: InventoryData;
  }> = ({ headCell, row }) => {
    const fieldValue = watch(headCell.id);
    const oldValue = headCell.getValue(row);

    let newValue = fieldValue;
    if (fieldValue != null) {
      newValue = headCell.formatValue
        ? headCell.formatValue(fieldValue)
        : fieldValue;
    }

    return (
      <TableCell key={headCell.id}>
        <TableTypography>{newValue}</TableTypography>
        <TableTypography
          sx={{
            textDecoration: "line-through",
            color: "red",
          }}
        >
          {oldValue}
        </TableTypography>
      </TableCell>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseConfirm}
      maxWidth="xl"
      fullWidth
      fullScreen={isPhoneSize}
    >
      <>
        <ConfirmCloseDialog />
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <DialogTitle sx={{ p: 0 }}>
              {t("inventory.batchEditTitle").replace(
                "{0}",
                selectedProductIds.length.toString()
              )}
            </DialogTitle>
            <IconButton
              onClick={handleCloseConfirm}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                "&:hover": { cursor: "pointer" },
              }}
            >
              <CloseRounded />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit(handleSaveChanges)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  ...(!isPhoneSize && {
                    gridTemplateColumns: "repeat(4, 1fr)",
                  }),
                  gap: 2,
                }}
              >
                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      select
                      {...field}
                      name="status"
                      id="status"
                      label={`Status ${t("inventory.leaveBlankToKeep")}`}
                      fullWidth
                      sx={{ height: "min-Content" }}
                      value={field.value == undefined ? "" : field.value}
                      onChange={(e) => {
                        const val =
                          e.target.value == "KEEP" ? undefined : e.target.value;
                        field.onChange(val);
                      }}
                    >
                      <MenuItem value={"KEEP"}>
                        {t("inventory.keepOriginal")}
                      </MenuItem>
                      {productStatusOptions.map((i) => (
                        <MenuItem key={i.value} value={i.value}>
                          {i.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Controller
                    name="location"
                    control={control}
                    rules={inventoryRules.locationRule}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="location"
                        name="location"
                        variant="filled"
                        error={!!error}
                        helperText={
                          <>
                            {/* Counter */}
                            {`${field?.value?.length || 0}/${
                              inventoryRules.locationRuleMaxLength
                            }`}

                            {/* Error */}
                            {error?.message &&
                              `. ${t(error.message).replace(
                                "{0}",
                                inventoryRules.locationRuleMaxLength.toString()
                              )}`}
                          </>
                        }
                        label={`${t("inventory.location")} ${t(
                          "inventory.leaveBlankToKeep"
                        )}`}
                        fullWidth
                      />
                    )}
                  />
                  <Button
                    onClick={() => {
                      if (
                        rowsSelected.filter((r) => r.location != "-").length > 0
                      ) {
                        setValue("location", null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    variant="contained"
                    sx={{ width: "fit-content" }}
                  >
                    {t("inventory.clear")}
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Controller
                    name="note"
                    control={control}
                    rules={inventoryRules.noteRule}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="note"
                        name="note"
                        variant="filled"
                        error={!!error}
                        helperText={
                          <>
                            {/* Counter */}
                            {`${field?.value?.length || 0}/${
                              inventoryRules.noteRuleMaxLength
                            }`}

                            {/* Error */}
                            {error?.message &&
                              `. ${t(error.message).replace(
                                "{0}",
                                inventoryRules.noteRuleMaxLength.toString()
                              )}`}
                          </>
                        }
                        label={`${t("inventory.note")} ${t(
                          "inventory.leaveBlankToKeep"
                        )}`}
                        fullWidth
                      />
                    )}
                  />
                  <Button
                    onClick={() => {
                      if (rowsSelected.filter((r) => r.note).length > 0) {
                        setValue("note", null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    variant="contained"
                    sx={{ width: "fit-content" }}
                  >
                    {t("inventory.clear")}
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Controller
                    name="plan"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        id="plan"
                        name="plan"
                        label={`${t("inventory.plan")} ${t(
                          "inventory.leaveBlankToKeep"
                        )}`}
                        fullWidth
                        sx={{ height: "min-Content" }}
                        value={
                          field.value == null ? "" : field.value.inventoryPlanId
                        }
                        onChange={(e) => {
                          let val: PlanTypes | undefined = undefined;

                          if (e.target.value != "KEEP") {
                            const obj: PlanTypes | undefined = plans.find(
                              (p) =>
                                p.inventoryPlanId == parseInt(e.target.value)
                            );
                            if (obj) val = obj;
                          }

                          field.onChange(val);
                        }}
                      >
                        <MenuItem value={"KEEP"}>
                          {t("inventory.keepOriginal")}
                        </MenuItem>
                        {plans.map((p) => (
                          <MenuItem
                            key={p.inventoryPlanId}
                            value={p.inventoryPlanId}
                          >
                            {p.companyName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Button
                    onClick={() => {
                      if (
                        rowsSelected.filter((r) => r.plan?.inventoryPlanId)
                          .length > 0
                      ) {
                        setValue("plan", null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    variant="contained"
                    sx={{ width: "fit-content" }}
                  >
                    {t("inventory.clear")}
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ width: "fit-content" }}
                  disabled={!isDirty}
                >
                  {t("buttons.save")}
                </Button>
              </Box>
            </Box>
          </form>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => {
                    if (headCell.id === "actions") return;
                    return (
                      <TableCell key={headCell.id}>
                        {t(headCell.label)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {rowsSelected.map((row) => {
                  return (
                    <TableRow key={row.productId}>
                      {headCells.map((headCell) => {
                        if (
                          headCell.id === "actions" ||
                          headCell.id === "histories"
                        )
                          return;

                        if (
                          headCell.editable === true &&
                          dirtyFields[headCell.id]
                        ) {
                          return (
                            <AdvEditTableCell
                              key={headCell.id}
                              headCell={headCell}
                              row={row}
                            />
                          );
                        }

                        const value = headCell.getValue(row);

                        return (
                          <TableCell key={headCell.id}>
                            <TableTypography>{value}</TableTypography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </>
    </Dialog>
  );
};

export default BatchEditPopup;
